<template>
	<div>
		<v-container style="max-width:1024px">
			<div style="padding-top:8px" />
			<router-link :to="lng.backRoute">
				<v-btn flat class="back-button">
					<v-icon>chevron_left</v-icon> {{ lng.back }}
				</v-btn>
			</router-link>
			<div id="sidebar" class="hidden-xs-only top">
				<div class="content">
					<div v-for="heading in headings" :key="heading.id" v-scroll-to="'#' + heading.id">
						<a :class="(heading.id == viewedHeading ? 'viewed-heading-' : 'unviewed-heading-') + heading.nodeName">{{ heading.text }}</a>
					</div>
				</div>
			</div>
			<div id="main">
				<div class="content elevation-2">
					<div v-if="language == 'en'">
						<v-container id="mainDoc" ref="mainDoc">
							<h1>HAQCS — Hapla Air Quality Control System</h1>
							<h2 ref="heading_0" id="heading_0">Purpose</h2>
							<div>The Hapla Air Quality Control System monitor (HAQCS monitor) fulfills two functions:</div>
							<ul>
								<li>It is an air quality indicator and warning device. It also shows the room climate on a comfort diagram. Alarms are triggered if the limits for healthy climate and good breathing air quality are exceeded. This acts as a reminder to ventilate.</li>
								<li>In conjunction with devices that can influence the air quality (such as ventilation devices or fans) or improve the indoor climate (humidifiers), it controls these devices as required.</li>
							</ul>
							<p>The HAQCS monitor is a guardian of the good air properties in the home, at work and at school. Wherever people spend a long time and, above all, have to stay focused. Good air quality is a prerequisite for this.</p>
							
							<h2 ref="heading_1" id="heading_1">Installation</h2>
							<p> The device is intended for wall mounting. It can also be placed on flat surfaces such as a desk, a sideboard and the like. It should not be placed in close proximity to people, but rather a little off. This space should have the same air quality as the rest of the room (no unventilated corners), but it should also not be exposed to direct drafts. </p>
							<p> The device should not be exposed to direct sunlight as the CO<sub>2</sub> sensor, which is based on the optical principle, could be disturbed. </p>
							<p> The power is supplied by cable from a 5V USB power supply. There are 2 micro USB sockets on the HAQCS monitor. This way it is possible to connect devices hanging on the wall from below, while the other socket is on the side and is intended to be used for that are placed using the pedestal. </p>
	
	
							<h2 ref="heading_2" id="heading_2">Usage as an air quality monitor</h2>
							<div class="between-headings"/>
							
							<h3 ref="heading_3" id="heading_3">Internal sensors</h3>
							<div> The device uses its internal sensors to measure the following indoor air parameters: </div>
							<ul>
								<li class = "narrow"> Temperature </li>
								<li class = "narrow"> Relative humidity </li>
								<li class = "narrow"> CO<sub>2</sub> level </li>
								<li class = "narrow"> VOC level as CO<sub>2</sub> equivalent </li>
							</ul>
							<p> The first two sizes have an impact on the well-being of people in rooms. Low humidity can lead to health problems due to the mucous membranes drynig out, while moisture that is too high becomes a danger to the building structure. If the humidity is too high, mold can form, thus posing a health hazard for humans. Hygienists recommend a relative humidity in the range of 40 - 60%. </p>
							<p> The <b>CO<sub>2</sub> level</b> shows how used up the air is. In living and working spaces, the CO<sub>2</sub> comes from people's breath. The concentration, which is approximately 400 ppm in fresh air, increases when people are present. The concentration of 1500 ppm is the upper limit for intellectual work and learning because it already leads to concentration problems. Exceeding the second threshold of 2500 ppm leads to headaches and other health problems with prolonged exposure. </p>
							<p> <b> VOC or TVOC – (Total) Volatile Organic Compounds </b> is a generic term for organic substances in the air. They arise in part through evaporation of the human body, but can have a different origin. E.g. Kitchen smells, exudation of furniture and varnishes count among them. </p>
	
							<h3 ref="heading_4" id="heading_4"> Weighting of CO<sub>2</sub> and TVOC </h3>
							<p> The result of the air quality measurement is a number. The CO<sub>2</sub> concentration is measured in ppm (<b>p</b>arts <b>p</b>er <b>m</b>illion). The value of 400 ppm corresponds to the clean, fresh air. </p>
							<p> The TVOC concentration is converted into a CO<sub>2</sub> equivalent after the measurement. The device enables the two sizes to be weighted according to personal priorities. The display and alarms are based on the weighted size. Settings from 100% CO<sub>2</sub>, 0% TVOC to 50% CO<sub>2</sub>, 50% TVOC are possible. The CO<sub>2</sub> concentration, as the more important variable, is taken into account with at least 50%. </p>
							<p> Read about the setting of the weighting between CO<sub>2</sub> and TVOC under <span class="link" v-scroll-to="'#heading_11'"> mode</span>. </p>
	
	
							<h3 ref="heading_5" id="heading_5">Display</h3>
							<p> The screen turns on when it is pressed. In addition, the device has a light display above the screen. </p>
							<p> After 35 seconds of inactivity the screen darkens and then goes out. If you'd like to prevent this, click on the symbol in the top right corner. Clicking this icon again turns the screen off. </p>
							
							<img src="../../assets/docs/air_quality_header.png"/>
	
							<p>After 2 hours, the screen turns off even when the icon is lit.</p>
	
							<h4 ref="heading_6" id="heading_6">Current air quality</h4>
	
							<p> The temperature and relative humidity are shown on the left edge of the screen. </p>
							<p> That includes the air quality as a number in ppm, which is the weighted value (see <span class="link" v-scroll-to="'#heading_4'"> Weighting of CO<sub>2</sub> and TVOC</span>). At the bottom this number is shown in the form of a graph on a colored scale. Additionally, the air quality can be recognized even when the screen is switched off, by the color and the position of the lights on the display above the screen. The displayed value is a moving average from the last 2 minutes. </p>
	
							<img src="../../assets/docs/comfort_screen_monitor_mode.png"/>
	
							<h4 ref="heading_7" id="heading_7">Current indoor climate</h4>
	
							<p>Clicking on the "comfort" button calls up the comfort graph, on which the current values of temperature and relative humidity are marked. The yellow field marks the conditions that are most optimal for the average person. The blue area shows the still acceptable conditions. Outside, most people complain that the indoor climate is too extreme.</p>
	
							<h4 ref="heading_8" id="heading_8">History</h4>
	
							<p> By clicking on the "CO<sub>2</sub> history" button, the course of the air quality in recent times is shown. </p>
							<p> "Short history" shows as a colored area the course of CO<sub>2</sub> concentration in ppm within the last 3 hours. The black line represents the TVOC concentration as a CO<sub>2</sub> equivalent in ppm. </p>
							<p> The current values are on the left edge of the screen. </p>
	
							<!-- <p>Bild mit Diagramm „short history“	Bild mit Diagramm „long history“</p> -->
							<!-- TODO -->
	
							<p>"Long history" shows the same sizes within the last 12 hours.</p>
							
							<h3 ref="heading_9" id="heading_9">Alarms</h3>
							<p style="font-style:italic">currently undocumented</p>
							<!-- TODO -->
	
							<h3 ref="heading_10" id="heading_10">Settings</h3>
							<p>The HAQCS monitor features a number of settings that may be tweaked by the user. The settings menu can be opened by clicking the gear icon in the top left corner of the screen.</p>
							<img src="../../assets/docs/air_quality_header.png"/>
							<p>The following subject areas are available:</p>
							<img src="../../assets/docs/settings.png"/>
	
							<h4 ref="heading_11" id="heading_11">mode</h4>
							<p>The weighting between CO<sub>2</sub> and TVOC can be carried out here. The displayed air quality, whether as a number or as the color and position of the chain of lights above the screen, is based on this setting.</p>
							<img src="../../assets/docs/mode_monitor_mode.png"/>
							<p>Move the marker to the desired position on the slider. On the far left only CO<sub>2</sub> is taken into account. The most-right position represents a weighting of 50:50.</p>
	
							<h4 ref="heading_12" id="heading_12">brightness</h4>
							<p>Here the brightness of the LED bar above the screen can be adjusted.</p>
							<img src="../../assets/docs/brightness.png"/>
	
							<p>
								<i>„Enable CO<sub>2</sub> indicator lights“</i> <br>
								Allows the LED display to be switched off if it were to be bothersome.
							</p>
	
							<p>
								<i>„LED strip brightness</i>“<br>
								Lets the brightness be adjusted to personal preferences. Allows you to change the automatically calculated brightness in the range of 50% - 200%.
							</p>
	
							<p>
								<i>„Adjust LED strip brightness automatically</i>“<br>
								Toggles the automatic adjustment of the brightness to the ambient light.
							</p>
	
							<p>
								<i>„Disable CO<sub>2</sub> indicator lights in the dark</i>“<br>
								This automatically switches off the LED display in dark surroundings so that it does not light up during the night.
							</p>
	
							<h4 ref="heading_13" id="heading_13">devices</h4>
							<p>This menu enables ventilation units to be connected and manages the connected units. This menu has no meaning when using the HAQCS monitor only as an air quality monitor.</p>
	
							<h3 ref="heading_14" id="heading_14">Compatible devices</h3>
							<p>You can find a list of compatible devices on the Internet at:</p>
	
							<a href="https://www.hapla.eu/offer" class="link" target="_blank">www.hapla.eu/offer</a><br><br>
	
							<p>Even simple 230 V fans, possibly already in use, can be controlled with the HAQCS monitor. For this purpose UniBox devices are connected to the power supply of the fans. Then they receive the control commands and use their internal power controller to adjust the speed of the fans and switch them on and off accordingly.</p>
	
							<h3 ref="heading_15" id="heading_15">Connect to devices</h3>
							<p> The HAQCS monitor is equipped with internal sensors and measures the air quality in the area in which it was installed. Therefore, it should control ventilation devices that ensure air quality in this area. It should not control ventilation in other areas because it lacks the measurement data for other rooms. </p>
							<p> If the ventilation of several areas of a house is to be controlled automatically, a HAQCS monitor must be set up in each of these areas. </p>
							<p> A HAQCS monitor can also capture large rooms and control several devices (up to 16) at the same time. The ventilation of a restaurant or an open-plan office can therefore be controlled by a HAQCS monitor. </p>
							<p> In order for the ventilation units to be controlled, they must be connected to the HAQCS monitor. To establish the connection, proceed as follows: </p>
	
							<ol>
								<li> Activate the ventilation unit to connect. To do this, follow the instructions of your ventilation unit. </li>
								<li> In HAQCS-Monitor go to the settings (button with the gear symbol in the upper left corner) and then to "devices" </li>
								<li> You will see a (probably still empty) list of connected devices. To add a new device click on the blue "+" symbol. </li>
								<li> A list of the devices that can be connected appears. The devices have a serial number that is shown in the list. Select the device you'd like to connect. </li>
								<li> You may have to enter a password to establish the connection. </li>
							</ol>
							<p> Repeat this for all devices that are to be controlled by the HAQCS monitor. </p>
							<p> You can also remove devices from the list. To do this, click on the device in the list (gear symbol) and follow the instructions. </p>
							
							<h3 ref="heading_16" id="heading_16">Operation and mode selection</h3>
							<p> The ventilation units can work in 3 automatic modes, be controlled manually by the user, be particularly quiet in the night mode or ventilate intensively in the "storm mode". A cooling effect is achieved when the devices are running in the summer mode. The targeted ventilation during periods of high humidity prevents mold growth. </p>
							<p> The operating mode is selected in the upper screen bar by scrolling left or right with the directional arrows. </p>
							<!-- <p>Bild Obere Leiste mit Modi</p> -->
							<!-- TODO -->
	
	
							<h4 ref="heading_17" id="heading_17">Automatic mode</h4>
							<p>There are 3 automatic modes. The control of the fan speed depends on the pollutant concentration as follows:</p>
							<img src="../../assets/docs/automatic_modes.png" class="no-shadow"/>
							<p> If the air quality is good, the ventilation units are switched off as ventilation is not necessary. If the threshold concentration C1, C2 or C3 is exceeded, the ventilation units start at minimum speed. Then there is a range in which the speed depends linearly on the pollutant concentration. At even higher concentrations, the speed remains at maximum. </p>
							<p> "AUTO (fresh)" is designed for maximum ventilation, "AUTO (silent)" for quiet operation with drawbacks in the air flow. "AUTO" is a compromise between the two. For a given average concentration, the speed difference between the levels is 10%, i.e. between "fresh" and "silent" 20%. </p>
							<p> Devices that do not support a stepless speed control switch the speed in stages and simulate the curves as best they can. </p>
	
							<h4 ref="heading_18" id="heading_18">Manual control</h4>
							<p> The manual control enables the user to control the speed of the ventilation units between 0% (the minimum speed of the specific model) and 100% (maximum speed) in steps of 10%. The devices then operate in an uncontrolled manner. The control takes place with the help of the "+" and "–" buttons. </p>
							<!-- <p>Bild Manual Mode</p> -->
							<!-- TODO -->
	
	
							<h4 ref="heading_19" id="heading_19">Night mode</h4>
							<p>The night mode focuses on the smooth running of the ventilation units. They run constantly at the minimum speed and are therefore uncontrolled.</p>
	
	
							<h4 ref="heading_20" id="heading_20">Intensive mode</h4>
							<p> The intensive mode (a.k.a. the "storm mode") is used to ventilate rooms intensively. The connected ventilation units then operate at full power for a certain amount of time (which can be one of 10, 20 or 30 minutes) and then return to the previous mode. </p>
							<p> It is advisable to activate the intensive mode for a break at school or during meetings, during the lunch break. Or at home when you leave the house, after cooking, etc. </p>
	
							<h4 ref="heading_21" id="heading_21">Summer mode</h4>
							<p> The summer mode enables ventilation without heat recovery. The idea is to let the fresh air flow into the rooms from the cooler side of the house and discharge them in other directions. For this purpose either two ventilation units or a single ventilation unit and a partially open window are utilized. This creates a certain natural cooling effect. </p>
	
							<p class="message">
								<b> Attention: </b> <br>
								Do not use if you are using air conditioning. <br>
								Then the cold recovery is mandatory.
							</p>
	
							<p> Ventilation units that normally change their conveying direction periodically only work in one direction in summer mode (the direction can be set on the unit). For ventilation units that are equipped with 2 fans and normally supply air and extract air at the same time, one of the fans is switched off (selection on the unit). </p>
							<!-- <p> Read about setting the summer mode in chapter ####. </p> -->
							<!-- TODO -->
	
	
							<h4 ref="heading_22" id="heading_22">Dehumidification</h4>
							<p> The ventilation removes the excess moisture from the rooms. However, situations are possible in which the air quality requires little or no ventilation, but the humidity requires more intensive ventilation. The HAQCS monitor automatically increases the air volume when the set threshold humidity is exceeded. The ventilation units are informed that they are working in dehumidification mode so that they can react accordingly.</p> <!--You can read about setting the threshold moisture in chapter ####. </p>--> <!-- TODO -->
							<p> The regulation follows the following algorithm: </p>
							<p> If the set threshold for humidity "F" is reached, the dehumidification mode is activated automatically. The controller calculates the appropriate speed based on the relative humidity. If this results in a higher speed than the quality measurement, this higher speed is transmitted to the ventilation units. </p>
							<img src="../../assets/docs/humidity_threshold.png" class="no-shadow"/> <!-- TODO: translate to English -->
	
	
							<h4 ref="heading_23" id="heading_23">Power failure</h4>
							<p>After changing the mode, the new setting is saved permanently after 5 minutes. If the power supply is interrupted, the HAQCS monitor then starts with the saved settings and the operating mode is retained.</p> <!-- TODO -->
	
						</v-container>
					</div>
					<div v-if="language == 'de'">
						<v-container id="mainDoc" ref="mainDoc">
							<h1>HAQCS — Hapla Air Quality Control System</h1>
							<h2 ref="heading_0" id="heading_0">Bestimmung</h2>
							<div>Der Monitor vom Hapla Air Quality Control System (HAQCS-Monitor) erfüllt zwei Funktionen:</div>
							<ul>
								<li>Er ist ein Luftqualitäts-Anzeiger und -Warner. Er zeigt zudem das Raumklima auf einem Behaglichkeitsdiagram an. Bei Überschreitung der Grenzwerte für gesundes Klima und gute Atemluftqualität werden Alarme ausgelöst. Damit wird an das Lüften erinnert.</li>
								<li>In Verbindung mit Geräten, die die Luftqualität beeinflussen können (Lüftungsgeräte, Ventilatoren) oder das Raumklima verbessern können (Luftbefeuchter) steuert er diese Geräte bedarfsgerecht.</li>
							</ul>
							<p>Der HAQCS-Monitor ist ein Bewacher der guten Lufteigenschaften in der Wohnung, am Arbeitsplatz und in der Schule. Überall dort, wo Menschen längere Zeit verbringen und vor Allem konzentriert bleiben müssen. Gute Luftqualität ist dafür Voraussetzung.</p>
							
							<h2 ref="heading_1" id="heading_1">Aufstellung</h2>
							<p>Das Gerät ist für die Wandmontage vorgesehen. Außerdem kann es auf ebenen Flächen, wie einem Schreibtisch, einem Sideboard und Ähnlichem aufgestellt werden. Es soll nicht in direkter Nachbarkeit der Personen, sondern etwas abseits sein. Dieser Platz soll die gleiche Luftqualität aufweisen, wie der Rest des Raumes (keine ungelüfteten Ecken), aber auch nicht dem direkten Luftzug ausgesetzt sein.</p>
							<p>Das Gerät soll nicht direkten Sonnenstrahlen ausgesetzt sein – der CO<sub>2</sub>-Sensor, der auf optischem Prinzip basiert, könnte dadurch gestört sein.</p>
							<p>Die Stromversorgung erfolgt per Kabel von einem 5V USB-Netzgerät. Am HAQCS-Monitor befinden sich 2 Mikro-USB-Buchsen. So ist es möglich an der Wand hängende Geräte von unten anzuschließen, während die andere Buchse sich an der Seite befindet und bei aufgestellten Geräten verwendet wird.</p>
	
	
							<h2 ref="heading_2" id="heading_2">Verwendung als Luftqualitätsmonitor</h2>
							<div class="between-headings"/>
							
							<h3 ref="heading_3" id="heading_3">Interne Sensoren</h3>
							<div>Das Gerät misst mit seinen internen Sensoren die folgenden Parameter der Raumluft:</div>
							<ul>
								<li class="narrow">Temperatur</li>
								<li class="narrow">Relative Feuchte</li>
								<li class="narrow">CO<sub>2</sub>-Pegel</li>
								<li class="narrow">VOC-Pegel als CO<sub>2</sub>-Äquivalent</li>
							</ul>
							<p>Die zwei ersten Größen haben Einfluss auf das Wohlbefinden der Menschen in Räumen. Zu niedrige Feuchte kann zu gesundheitlichen Problemen führen, weil die Schleimhäute austrocknen, während eine zu hohe Feuchte zur Gefahr für die Bausubstanz wird. Bei zu hoher Feuchte können sich Schimmel bilden, was ebenfalls gesundheitsgefährdend für den Menschen ist. Hygieniker empfehlen eine relative Feuchte im Bereich 40 – 60%.</p>
							<p>Der <b>CO<sub>2</sub>-Pegel</b> zeigt wie verbraucht die Luft ist. In Wohn- und Arbeitsräumen stammt das CO<sub>2</sub> aus dem Atem der Menschen. Die Konzentration, die in frischer Luft ca. 400 ppm beträgt, steigt bei Anwesenheit von Menschen. Die Konzentration von 1500 ppm gilt als Obergrenze für geistige Arbeit und das Lernen, weil sie bereits zu Konzentrationsstörungen führt. Die Überschreitung der zweiten Schwelle von 2500 ppm führt bei längerer Exposition zu Kopfschmerzen und anderen gesundheitlichen Beeinträchtigungen.</p>
							<p><b>VOC oder TVOC – (Total) Volatile Organic Compounds</b> ist ein Oberbegriff für organische Substanzen in der Luft. Sie entstehen z.T. durch Ausdunstungen des menschlichen Körpers, können aber einen anderen Ursprung haben. Z.B. Küchengerüche, Absonderungen der Möbel und Lacke zählen dazu. </p>
	
							<h3 ref="heading_4" id="heading_4">Gewichtung von CO<sub>2</sub> und TVOC</h3>
							<p>Das Ergebnis der Luftqualitätsmessung ist eine Zahl. Die CO<sub>2</sub>-Konzentration wird in ppm (<b>p</b>arts <b>p</b>er <b>m</b>illion) gemessen. Der Wert von 400 ppm entspricht der sauberen, frischen Luft.</p>
							<p>Die TVOC-Konzentration wird nach der Messung in ein CO<sub>2</sub>-Äquivalent umgerechnet. Das Gerät ermöglicht es die beiden Größen entsprechend den persönlichen Prioritäten zu gewichten. Die Anzeige und die Alarme basieren auf der gewichteten Größe. Möglich sind Einstellungen von 100% CO<sub>2</sub>, 0% TVOC bis 50% CO<sub>2</sub>, 50% TVOC. Die CO<sub>2</sub>-Konzentration, als die wichtigere Größe wird mit mindestens 50% berücksichtigt.</p>
							<p>Über die Einstellung der Gewichtung zwischen CO<sub>2</sub> und TVOC lesen Sie unter <span class="link" v-scroll-to="'#heading_11'">Arbeitsmodus (mode)</span>.</p>
	
	
							<h3 ref="heading_5" id="heading_5">Anzeige</h3>
							<p>Der Bildschirm schaltet sich nach einem Anklicken an. Darüber hinaus verfügt das Gerät über eine Lichtanzeige über dem Bildschirm.</p>
							<p>Nach 35 Sekunden verdunkelt sich der Bildschirm und geht danach aus. Will man das verhindern, soll das Symbol in der rechten oberen Ecke angeklickt werden. Das wiederholte Anklicken dieses Symbols schaltet den Bildschirm aus.</p>
							
							<img src="../../assets/docs/air_quality_header.png"/>
	
							<p>Nach 2 Stunden wird der Bildschirm auch dann ausgeschaltet, wenn das Symbol leuchtet.</p>
	
							<h4 ref="heading_6" id="heading_6">Aktuelle Luftqualität</h4>
	
							<p>An dem linken Rand des Bildschirms sind die Temperatur und die relative Feuchte angegeben.</p>
							<p>Darunter die Luftqualität als Zahl in ppm, wobei das der gewichtete Wert ist (siehe <span class="link" v-scroll-to="'#heading_4'">Gewichtung von CO<sub>2</sub> und TVOC</span>). Am unteren Rand ist diese Zahl in Form von einem Graf auf farbiger Skala dargestellt. Außerdem ist die Luftqualität auch bei ausgeschaltetem Bildschirm, an der Farbe und der Position der Lichter auf der Anzeige oberhalb des Bildschirms erkennbar. Der angezeigte Wert ist ein gleitender Durchschnitt aus den letzten 2 Minuten.</p>
	
							<img src="../../assets/docs/comfort_screen_monitor_mode.png"/>
	
							<h4 ref="heading_7" id="heading_7">Aktuelles Raumklima</h4>
	
							<p>Durch klicken auf die Schaltfläche „comfort“ wird der Behaglichkeitsgraf aufgerufen, auf dem die aktuellen Werte von Temperatur und relativer Feuchte eingetragen sind. Das gelbe Feld markiert die optimalen Bedingungen für die Menschen. Die blaue Fläche zeigt den noch akzeptablen Zustand. Außerhalb klagen die meisten Menschen über zu extremes Raumklima.</p>
	
							<h4 ref="heading_8" id="heading_8">Geschichte</h4>
	
							<p>Durch klicken auf die Schaltfläche „CO<sub>2</sub> history“ wird der Verlauf der Luftqualität in der letzten Zeit dargestellt.</p>
							<p>„Short history“ zeigt als eine farbige Fläche den Verlauf von CO<sub>2</sub>-Konzentration in ppm innerhalb der letzten 3 Stunden an. Die schwarze Linie soll die TVOC-Konzentration als CO<sub>2</sub>-Äquivalent in ppm darstellen.</p>
							<p>Am linken Bildrand sind die aktuellen Werte.</p>
	
							<!-- <p>Bild mit Diagramm „short history“	Bild mit Diagramm „long history“</p> -->
							<!-- TODO -->
	
							<p>„Long history“ zeigt die gleichen Größen in den letzten 12 Stunden.</p>
							
							<h3 ref="heading_9" id="heading_9">Alarme</h3>
							<p style="font-style:italic">momentan undokumentiert</p>
							<!-- TODO -->
	
							<h3 ref="heading_10" id="heading_10">Einstellungen</h3>
							<p>Um Einstellungen vorzunehmen klicken Sie auf das Zahnrad in der linken oberen Ecke des Bildschirms.</p>
							<img src="../../assets/docs/air_quality_header.png"/>
							<p>Folgende Themenbereiche Stehen zur Verfügung:</p>
							<img src="../../assets/docs/settings.png"/>
	
							<h4 ref="heading_11" id="heading_11">Arbeitsmodus (mode)</h4>
							<p>Hier kann die Gewichtung zwischen CO<sub>2</sub> und TVOC vorgenommen werden. Die angezeigte Luftqualität, ob als Zahl oder als Farbe und Position der Lichterkette oberhalb des Bildschirms, basiert auf dieser Einstellung.</p>
							<img src="../../assets/docs/mode_monitor_mode.png"/>
							<p>Verschieben Sie den Marker an dem Schieber in die gewünschte Position. Ganz links wird nur CO<sub>2</sub> berücksichtigt. Rechte Position bedeutet Gewichtung 50:50.</p>
	
							<h4 ref="heading_12" id="heading_12">Helligkeit (brightness)</h4>
							<p>Hier kann die Helligkeit der LED-Leiste über dem Bildschirm angepasst werden.</p>
							<img src="../../assets/docs/brightness.png"/>
	
							<p>
								<i>„Enable CO<sub>2</sub> indicator lights“</i> <br>
								Ermöglicht die LED-Anzeige auszuschalten, wenn sie stören sollte.
							</p>
	
							<p>
								<i>„LED strip brightness</i>“<br>
								Anpassung der Helligkeit an die persönlichen Präferenzen. Ermöglicht die automatisch berechnete Helligkeit im Bereich 50% - 200% zu ändern.
							</p>
	
							<p>
								<i>„Adjust LED strip brightness automatically</i>“<br>
								Schaltet die automatische Anpassung der Helligkeit an das Umgebungslicht.
							</p>
	
							<p>
								<i>„Disable CO<sub>2</sub> indicator lights in the dark</i>“<br>
								Damit erfolgt automatisches Abschalten der LED-Anzeige in dunkler Umgebung, damit sie nicht stört. 
							</p>
	
							<h4 ref="heading_13" id="heading_13">Verbundene Geräte (devices)</h4>
							<p>Dieses Menü ermöglicht Lüftungsgeräte anzuschließen und verwaltet die angeschlossenen Geräte. Bei der Nutzung des HAQCS-Monitors nur als Luftqualitäts-Monitor hat dieses Menu keine Bedeutung.</p>
	
							<h3 ref="heading_14" id="heading_14">Kompatible Geräte</h3>
							<p>Die Liste der kompatiblen Geräte finden Sie im Internet unter der Adresse:</p>
	
							<a href="https://www.hapla.eu/angebot" class="link" target="_blank">www.hapla.eu/angebot</a><br><br>
	
							<p>Auch einfache 230 V Ventilatoren, eventuell bereits im Einsatz, lassen sich mit HAQCS-Monitor regeln. Dafür werden UniBox-Geräte in die Stromzuleitung zu den Ventilatoren geschaltet. Dann empfangen sie die Steuerbefehle und mittels enthaltenen Leistungsreglers verstellen Sie die Drehzahl der Ventilatoren und schalten sie ein und aus.</p>
	
							<h3 ref="heading_15" id="heading_15">Verbinden mit Geräten</h3>
							<p>Der HAQCS-Monitor ist mit internen Sensoren ausgestattet und misst die Luftqualität in diesem Bereich in dem er montiert wurde. Daher soll er Lüftungsgeräte steuern, die für die Luftqualität in diesem Bereich sorgen. Er soll nicht die Lüftung in anderen Bereichen regeln, da ihm die Messdaten für andere Räume fehlen. </p>
							<p>Soll die Lüftung mehrerer Bereiche eines Hauses automatisch geregelt werden, muss in jedem dieser Bereiche ein HAQCS-Monitor aufgestellt sein. </p>
							<p>Ein HAQCS-Monitor kann aber auch große Räume erfassen und mehrere Geräte (bis zu 16) gleichzeitig steuern. So kann die Lüftung eines Restaurants oder eines Großraumbüros von einem HAQCS-Monitor gesteuert werden.</p>
							<p>Damit die Lüftungsgeräte geregelt werden können, müssen sie mit einem HAQCS-Monitor verbunden werden. Um die Verbindung herzustellen, gehen Sie folgendermaßen vor:</p>
	
							<ol>
								<li>Aktivieren Sie das Lüftungsgerät zum Verbinden. Folgen Sie dazu der Instruktion Ihres Lüftungsgeräts. </li>
								<li>Gehen Sie in HAQCS-Monitor in die Einstellungen (Schaltfläche mit der Zahnradsymbol in der oberen linken Ecke) und dann zu „devices“</li>
								<li>Sie sehen eine (wahrscheinlich noch leere) Liste der verbundenen Geräte. Um ein weiteres Gerät hinzuzufügen klicken Sie auf das blaue „ + “ Symbol.</li>
								<li>Es erscheint eine Liste der Geräte, die verbunden werden können. Die Geräte verfügen über eine Seriennummer, die in der Liste angezeigt wird. Wählen Sie das Gerät, das Sie verbinden wollen, aus. </li>
								<li>Eventuell müssen Sie noch ein Password eingeben, um die Verbindung zu errichten.</li>
							</ol>
							<p>Auf diese Weise verbinden Sie alle Geräte, die von dem HAQCS-Monitor geregelt werden sollen.</p>
							<p>Sie können auch Geräte aus der Liste wieder entfernen. Klicken Sie dazu das Gerät in der Liste an (Zahnradsymbol) und folgen Sie den Instruktionen.</p>
							
							<h3 ref="heading_16" id="heading_16">Funktionsweise und Moduswahl</h3>
							<p>Die Lüftungsgeräte können in 3 automatischen Modi arbeiten, durch den Benutzer manuell gesteuert werden, im Nachtmodus besonders laufruhig sein oder im „storm mode“ zeitgesteuert intensiv lüften. Im Sommermodus wird ein Kühleffekt erzielt. Gezielte Lüftung bei hoher Luftfeuchte verhindert Schimmelbildung.</p>
							<p>Der Betriebsmodus wird in der oberen Bildschirmleiste ausgewählt, indem man nach links oder rechts mit den Richtungspfeilen scrollt.</p>
							<!-- <p>Bild Obere Leiste mit Modi</p> -->
							<!-- TODO -->
	
	
							<h4 ref="heading_17" id="heading_17">Automatikmodus</h4>
							<p>Es gibt 3 automatische Modi. Die Regelung der Drehzahl der Lüfter ist folgendermaßen von der Schadstoff-Konzentration abhängig:</p>
							<img src="../../assets/docs/automatic_modes.png" class="no-shadow"/>
							<p>Bei guter Luftqualität werden die Lüftungsgeräte abgeschaltet – Lüftung ist nicht notwendig. Wird die Schwellenkonzentration C1, C2 oder C3 überschritten, starten die Lüftungsgeräte mit minimaler Drehzahl. Dann folgt ein Bereich, in dem die Drehzahl linear von der Schadstoffkonzentration abhängt. Bei noch höheren Konzentrationen bleibt die Drehzahl bei Maximum.</p>
							<p>„AUTO (fresh)“ ist auf maximale Lüftung ausgelegt, „AUTO (silent)“ auf leisen Betrieb bei Abstrichen bei dem Luftstrom. „AUTO“ ist ein Kompromiss zwischen den beiden. Bei einer gegebenen mittleren Konzentration beträgt der Abstand der Drehzahlen zwischen den Stufen 10%, also zwischen „fresh“ und „silent“ 20%.</p>
							<p>Geräte, die eine stufenlose Drehzahlregelung nicht unterstützen, schalten die Drehzahl in Stufen und bilden die Kurven so gut sie können nach.</p>
	
							<h4 ref="heading_18" id="heading_18">Manuelle Steuerung</h4>
							<p>Die manuelle Steuerung ermöglicht dem Benutzer die Drehzahl der Lüftungsgeräte zwischen 0% (die Minimaldrehzahl des bestimmten Modells) und 100% (Maximaldrehzahl) in Schritten je 10% zu steuern. Die Geräte laufen dann ungeregelt. Die Steuerung erfolgt mit Hilfe der Schaltflächen „ + “ und „ - “.</p>
							<!-- <p>Bild Manual Mode</p> -->
							<!-- TODO -->
	
	
							<h4 ref="heading_19" id="heading_19">Nachtmodus</h4>
							<p>Im Nachtmodus wird der Schwerpunkt auf die Laufruhe der Lüftungsgeräte gelegt. Sie laufen konstant mit der minimalen Drehzahl, also ungeregelt.</p>
	
	
							<h4 ref="heading_20" id="heading_20">Intensivmodus</h4>
							<p>Der Intensivmodus „storm mode“ wird benutzt, um Räume intensiv zu lüften. Die verbundenen Lüftungsgeräte laufen dann eine gewisse Zeit mit voller Leistung und kehren danach in den vorherigen Modus zurück. </p>
							<p>Es empfiehlt sich den Intensivmodus für die Dauer einer Pause in der Schule oder bei Besprechungen, in der Mittagspause zu aktivieren. Oder zu Hause, wenn man das Haus verlässt, oder nach dem Kochen, usw.</p>
	
							<h4 ref="heading_21" id="heading_21">Sommer-Modus</h4>
							<p>Der Sommermodus ermöglicht eine Lüftung ohne Wärmerückgewinnung. Die Idee ist, die frische Luft von der kühleren Seite des Hauses in die Räume strömen zu lassen und in andere Richtungen zu entlassen. Dafür werden 2 Lüftungsgeräte verwendet, oder ein Lüftungsgerät und ein teilweise offenes Fenster. Dadurch wird ein gewisser natürlicher Kühleffekt erzielt.</p>
	
							<p class="message">
								<b>Achtung:</b> <br>
								Nicht benutzen, wenn Sie eine Klimaanlage verwenden. <br>
								Dann ist die Kälte-Rückgewinnung obligatorisch.
							</p>
	
							<p>Lüftungsgeräte, die normalerweise ihre Förderrichtung periodisch wechseln, arbeiten im Sommermodus immer nur in eine Richtung (die Richtung ist einstellbar am Gerät). Bei Lüftungsgeräten, die mit 2 Lüftern ausgestattet sind und normalerweise Zuluft und Abluft gleichzeitig fördern, wird einer der Lüfter ausgeschaltet (Auswahl am Gerät).</p>
							<!-- <p>Über Einstellung des Sommermodus lesen Sie in Kapitel ####.</p> --> <!-- TODO -->
	
	
							<h4 ref="heading_22" id="heading_22">Entfeuchtung</h4>
							<p>Die Lüftung entsorgt die überschüssige Feuchte aus den Räumen. Es sind aber Situationen möglich, bei denen die Luftqualität keine oder nur geringe Lüftung erfordert, aber die Feuchte eine intensivere Lüftung erforderlich macht. Der HAQCS-Monitor erhöht automatisch die Luftmenge, wenn die eingestellte Schwellenfeuchte überschritten wird. Dabei werden die Lüftungsgeräte davon informiert, dass sie in Entfeuchtungsmodus arbeiten, damit sie darauf reagieren können.</p> <!--Über die Einstellung der Schwellenfeuchte lesen Sie im Kapitel ####.</p> --> <!-- TODO -->
							<p>Die Regelung erfolgt nach folgendem Algorithmus:</p>
							<p>Wird die eingestellte Schwelle für Feuchte „F“ erreicht, aktiviert sich automatisch der Entfeuchtungsmodus. Der Regler berechnet die passende Drehzahl anhand der relativen Feuchte. Ergibt sich dabei höhere Drehzahl, als aus der Qualitätsmessung, wird diese höhere Drehzahl an die Lüftungsgeräte übermittelt.</p>
							<img src="../../assets/docs/humidity_threshold.png" class="no-shadow"/>
	
	
							<h4 ref="heading_23" id="heading_23">Stromausfall</h4>
							<p>Nach einer Änderung des Modus, wird die neue Einstellung nach 5 Minuten dauerhaft gespeichert. Wird die Stromzufuhr unterbrochen, startet der HAQCS-Monitor danach mit den gespeicherten Einstellungen und der Betriebsmodus bleibt erhalten.</p> <!-- TODO -->
	
						</v-container>
					</div>
					<div v-if="language == 'pl'">
						<v-container id="mainDoc" ref="mainDoc">
							<h1>HAQCS — Hapla Air Quality Control System</h1>
							<h2 ref="heading_0" id="heading_0">Zastosowanie</h2>
							<div>Monitor systemu HAQCS (Hapla Air Quality Control System) określany dalej jako HAQCS-monitor spełnia dwie funkcje.</div>
							<ul>
								<li>Jest monitorem jakości powietrza z możliwością alarmowania. Oprócz tego pokazuje klimat w pomieszczeniu na diagramie komfortu. Przy przekroczeniu wartości granicznych alarmuje i przypomina o potrzebie wietrzenia.</li>
								<li>W połączeniu z urządzeniami wpływającymi na jakość powietrza (wentylatory, rekuperatory, oczyszczacze powietrza), albo na klimat w pomieszczeniu (nawilżacze, klimatyzatory) steruje tymi urządzeniami odpowiednio do potrzeb.</li>
							</ul>
							<p>HAQCS-monitor jest strażnikiem dobrych cech powietrza w mieszkaniu, miejscu pracy czy w szkole. Wszędzie tam, gdzie ludzie spędzają dużo czasu i przede wszystkim tam, dzie muszą oni się koncentrować. Dobra jakość powietrza jest tego warunkiem.</p>
							
							<h2 ref="heading_1" id="heading_1">Wybór miejsca</h2>
							<p>Urządzenie jest przewidziane do montażu na ścianie. Oprócz tego może zostać postawione na biurku, komodzie, szafce nocnej. Nie powienien znajdować się w bezpośredniej styczności z ludźmi – lecz być w pewniej odległości. Miejsce to powinno mieć te same właściwości powietrza, jak reszta pomieszczenia (nie np. zaułek ze stającym powietrzem), ale też nie w przeciągu.</p>
							<p>Miejsce ustawienia nie powinno być narażone na bezpośrednie działanie słońca by nie zakłócić działania sensora CO<sub>2</sub>,  który działa na zasadzie optycznej.</p>
							<p>Zasilenie odbywa się za pomocą kabla z 5-Voltowego zasilacza USB. Od spodu i z boku są gniazda mikro-USB umożliwiające podłączenie od dołu przy montażu na ścianie i z boku przy ustawieniu na powierzchniach poziomych.</p>
	
	
							<h2 ref="heading_2" id="heading_2">Zastosowanie jako monitor jakości powietrza</h2>
							<div class="between-headings"/>
							
							<h3 ref="heading_3" id="heading_3">Wewnętrzne sensory</h3>
							<div>HAQCS-monitor mierzy następujące parametry powietrza:</div>
							<ul>
								<li class="narrow">temperatuę</li>
								<li class="narrow">wilgotność względną</li>
								<li class="narrow">Poziom CO<sub>2</sub></li>
								<li class="narrow">Poziom VOC jako ekwiwalent CO<sub>2</sub></li>
							</ul>
							<p>Dwie pierwsze wielkości mają wpływ na samopoczucie ludzi w pomieszczeniach. Zbyt niskia wilgotność może prowadzić do problemów zdrowotnych powodując wysychanie błon śluzowych, natomiast zbyt wysoka wilgotność jest niebezpieczna dla substancji budowlanej. Przy dużej wilgotności dochodzi do pojawiania się pleśni, co też jest niebezpieczne dla ludzi. Higieniści polecają by utrzymywać wilgotność względną na poziomie 40 – 60%.</p>
							<p><b>Poziom dwutlenku węgla CO<sub>2</sub></b> informuje o stopniu zużycia powietrza. W pomieszczeniach mieszkalnych, w biurach, klasach szkolnych, CO<sub>2</sub> pochodzi z oddychania ludzi. Jego stężenie, które w świeżym powietrzu wynosi ca. 400 ppm rośnie w obecności ludzi. Stężenie 1500 ppm uznawane jest za górną granicę dla pracy umysłowej i nauce, ponieważ prowadzi do zakłóceń koncentracji. Przekroczenie drugiego progu 2500 ppm prowadzi przy dłuższej ekspozycji do bólu głowy i innych obciążeń zdrowotnych.</p>
							<p><b>VOC lub TVOC – (Total) Volatile Organic Compounds</b> jest ogólnym określeniem dla substancji organicznych w powietrzu. Powstają one częściowo jako emisję ludzkiego ciała, ale mogą mieć też inne źródło. Przykładami niech będą zapachy kuchenne, dym papierosowy, emisje mebli, farb i tworzyw sztucznych. </p>
	
							<h3 ref="heading_4" id="heading_4">Średnia ważona CO<sub>2</sub> i TVOC</h3>
							<p>Wynik pomiaru jakości powietrza jast liczbą. Stężenie CO<sub>2</sub> jest podawane w ppm (<b>p</b>arts <b>p</b>er <b>m</b>illion). Wartość 400 ppm odpowiada czystemu, świeżemu powietrzu.</p>
							<p>Stężenie TVOC zostaje po pomiarze przeliczone na ekwiwalent CO<sub>2</sub>. HAQCS-monitor umożliwia ustawienie średniej ważonej tych dwu wielkości w zależności od własnych priorytetów. Pokazywana jest średnia ważona i jest ona podstawą sterowania i regulacji wentylacji. Możliwe są ustawienia od 100% CO<sub>2</sub>, 0% TVOC (czyli tylko CO<sub>2</sub> jest brany pod uwagę) do 50% CO<sub>2</sub>, 50% TVOC. Stężenie CO<sub>2</sub>, jako ważniejsza wielkość jest brana pod uwagę w przynajmniej 50%.</p>
							<p>O ustawianiu średniej ważonej między CO<sub>2</sub> i TVOC traktuje punkt <span class="link" v-scroll-to="'#heading_11'">Tryb działania (mode)</span>.</p>
	
	
							<h3 ref="heading_5" id="heading_5">Wyświetlacz</h3>
							<p>Ekran dotykowy włącza się po naciśnięciu. Oprócz tego urządzenie dysponuje wyświetlaczem LED powyżej ekranu. Po 35 sekundach ekran się ściemnia a następnie wyłącza. </p>
							<p>Jeśli chce się temu zapobiec można kliknąć na symbol lampki w górnym prawym rogu. Powtórne kliknięcie wyłącza ekran.</p>
							
							<img src="../../assets/docs/air_quality_header.png"/>
	
							<p>Najpóźniej po 2 godzinach ekran się wyłącza też wtedy, gdy symbol jest aktywny.</p>
	
							<h4 ref="heading_6" id="heading_6">Aktualna jakość powietrza</h4>
	
							<p>Po lewej stronie ekranu przedstawione są temperatura i wilgotność względna.</p>
							<p>Poniżej jakość powietrza jako liczba w ppm, przy czym jest to średnia ważona z pomiarów CO<sub>2</sub> i TVOC z wagą ustaloną w ustawieniach (patrz punkt <span class="link" v-scroll-to="'#heading_4'">Średnia ważona CO<sub>2</sub> i TVOC</span>). Na dole ekranu jest ta liczba w postaci znacznika na kolorowej skali. Oprócz tego, też przy wyłączonym ekranie, można jakość powietrza poznać po kolorze i pozycji światła na wyświetlaczu LED powyżej ekranu. Przedstawiona wartość jest średnią kroczącą z pomiarów ostatnich 2 minut.</p>
	
							<img src="../../assets/docs/comfort_screen_monitor_mode.png"/>
	
							<h4 ref="heading_7" id="heading_7">Aktualny klimat w opomieszczeniu</h4>
	
							<p>Klikając na zakładkę „comfort“ wywołuje się diagram klimatu w pomieszczeniu, na którym przedstawione są aktualne wartości temperatury i wilgotności na tle diagramu samopoczucia. Żółte pole pokazuje obszar optymalnych parametrów dla ludzi. Niebieskie pole, to obszar akceptowalny. Poza nim większość ludzi skarży się na zbyt ekstremalny klimat.</p>
	
							<h4 ref="heading_8" id="heading_8">Historia</h4>
	
							<p>Po kliknięciu na zakładkę „CO<sub>2</sub> history“ ukazuje się przebieg parametrów powietrza w ostatnim czasie.</p>
							<p>„Short history“ pokazuje jako kolorowe pole przebieg stężenia CO<sub>2</sub> w ciągu ostatnich 3 godzin w ppm. Czarna linia to TVOC jako ekwiwalent CO<sub>2</sub>. Po lewej stronie – aktualny pomiar. </p>
	
							<!-- <p>Bild mit Diagramm „short history“	Bild mit Diagramm „long history“</p> -->
							<!-- TODO -->
	
							<p>„Long history“ pokazuje te same dane z ostatnich 12 godzin.</p>
							
							<h3 ref="heading_9" id="heading_9">Alarmy</h3>
							<p>Dwa alarmy zwracają uwagę na złą jakość powietrza akustycznie i optycznie. Sygnał akustyczny jest wprawdzie głośny, ale trwa poniżej 1 sekundy i nie zakłóca lekcji czy narady.</p>
							<p>Alarmy następują przy 1500 ppm i 2500 ppm CO<sub>2</sub>. </p>
	
							<h3 ref="heading_10" id="heading_10">Ustawienia</h3>
							<p>By przejść do ustawień kliknij na symbol koła zębatego w lewym górnym rogu ekranu.</p>
							<img src="../../assets/docs/air_quality_header.png"/>
							<p>Następujące tematy stoją do dyspozycji:</p>
							<img src="../../assets/docs/settings.png"/>
	
							<h4 ref="heading_11" id="heading_11">Tryb działania (mode)</h4>
							<p>Tutaj można ustawić wagę między CO<sub>2</sub> i TVOC. Wskazywana jakość powietrza, czy to jako liczba, czy za pomocą listwy LED bazuje na tym ustawieniu. Intensywność wentylacji jest też od tej wielkości zależna.</p>
							<img src="../../assets/docs/mode_monitor_mode.png"/>
							<p>Przesuń suwak w porządane miejsce. Całkiem z lewej strony tylko CO<sub>2</sub> będzie brane pod uwagę. Skrajna prawa pozycja oznacza wagę 50:50. </p>
	
							<h4 ref="heading_12" id="heading_12">Jasność (brightness)</h4>
							<p>Tytaj można ustawić jasność listwy LED.</p>
							<img src="../../assets/docs/brightness.png"/>
	
							<p>
								<i>„Enable CO<sub>2</sub> indicator lights“</i> <br>
								Umożliwia wyłączenie listwy, jeśliby przeszkadzała.
							</p>
	
							<p>
								<i>„LED strip brightness</i>“<br>
								Dopasowanie jasności LED. Umożliwia korektę automatycznie ustawionej jasności w zakresie 50% - 200%.
							</p>
	
							<p>
								<i>„Adjust LED strip brightness automatically</i>“<br>
								Włącza automatyczny dobór jasności do warunków otoczenia.
							</p>
	
							<p>
								<i>„Disable CO<sub>2</sub> indicator lights in the dark</i>“<br>
								Umożliwia automatuczne wyłączanie listwy LED w ciemnym otoczeniu, np. w nocy, by nie przeszkadzała. 
							</p>
	
							<h4 ref="heading_13" id="heading_13">Połączone urządzenia (devices)</h4>
							<p>Tutaj następuje przyłączanie zewnętrznych urządzeń. Jeśli HAQCS-monitor jest używany tylko do pomiarów, to menu nie jest używane.</p>

							<h2 ref="heading_14" id="heading_14">Regulacja urządzeń wentylacyjnych</h2>
							<p>Czytaj rozdział 4 by zapoznać się z urządzeniem. Przedstawione tam informacje mają zastosowanie także przy używaniu HAQCS-Monitora do sterowania i regulacji urządzeń wentylacyjnych. </p>
							<p>HAQCS-monitor komunikuje się bezprzewodowo z urządzeniami wentylacyjnymi by je sterować. Sensory w jago wnętrzu umożliwiają regulację wentylacji odpowiednio do rzeczywistych aktualnych potrzeb. </p>
							<p>Komunikacja opiera się na technologii WiFi, ale bez połączenia z internetem. HAQCS-monitor i przyłączone urządzenia tworzą izolowaną wyspę. Router nie jest potrzebny. Nie ma też dostępu z zewnątrz.</p>
	
							<h3 ref="heading_15" id="heading_15">Kompatybilne urządzenia</h3>
							<p>Lista kompatybilnych urządzeń dostępna jest pod adresem:</p>
	
							<a href="https://www.hapla.eu/oferta" class="link" target="_blank">www.hapla.eu/oferta</a><br><br>
	
							<p>Też zwyczajne wentylatory na prąd przemienny 230 V, ewentualnie już zabudowane, można sterować za pomocą systemu HAQCS. W tym celu przyłącza się urządzenia HAQCS-Unibox do linii zasilającej dany wentylator. Unibox komunikuje się z HAQCS-Monitorem i jego sterownik mocy napędza odpowiednio wentylator regulując jego prędkość obrortową.</p>
	
							<h3 ref="heading_16" id="heading_16">Łączenie z urządzeniami</h3>
							<p>HAQCS-monitor jest wyposażony w sensory, przy pomocy których mierzy jakość powietrza w obszarze, w którym został zamontowany. Powiniem więc sterować urządzeniami wentylacyjnymi w tym samym obszarze. Nie powinien sterować urządzeń w innych obszarach, bo nie dysponuje danymi pomiarowymi z innych miejsc. </p>
							<p>Jeśli wentylacja danego domu ma być sterowana automatycznie w kilku obszarach, to w każdym z tych obszarów trzeba zamontować osobny HAQCS-monitor. </p>
							<p>Pojedynczy HAQCS-monitor może jednak objąć regulacją duże pomieszczenia, jak np. sale restauracyjne czy biura wielkopowierzchniowe i sterować jednocześnie wieloma urządzeniami (do 16-tu). </p>
							<p>Urządzenia, które mają być sterowane przez HAQCS-monitor muszą zostać z nim połączone. W celu połączenia należy: </p>
	
							<ol>
								<li>Aktywować dane urządzenie wentylacyjne do połączenia. Proszę stosować się w tym celu do instrukcji danego urządzenia.</li>
								<li>Kliknąć symbol ustawień w górnym lewym rogu, a następnie wybrać „devices“</li>
								<li>Widać (pewnie jeszcze pustą) listę połączonych urządzeń. Żeby dodać urządzenie należy kliknąć na niebieski symbol „ + “.</li>
								<li>Pojawia się lista urządzeń, które są gotowe do połączenia. Wybierz to, które chcesz połączyć i kliknij „connect”. </li>
								<li>Ewentualnie trzeba jeszcze podać hasło, by połączenie było możliwe.</li>
							</ol>
							<p>W ten sposób należy połączyć wszystkie urządzenia, które HAQCS-monitor ma kontrolować.</p>
							<p>Można też usunąć urządzenia z listy. Kliknij na symbol ustawień danego urządzenia.</p>
							
							<h3 ref="heading_17" id="heading_17">Wybór trybu pracy</h3>
							<p>Urządzenia wentylacyjne mogą pracować 3 trzech trybach automatycznych, mogą być sterowane ręcznie, w trybie nocnym być szczególnie ciche, albo w „storm mode“ intensywnie wietrzyć przez zadany czas. W trybie letnim wywołują pewien pasywny efekt chłodzenia. Specjalny tryb osuszania włącza się po przekroczeniu ustawionego progu wilgotności, by zapobiec powstawaniu pleśni.</p>
							<p>Tryb pracy ustawiany jest na listwie górnej wyświetlacza przez przewijanie za pomocą przyciskó ze strzałkami.</p>
							<!-- <p>Bild Obere Leiste mit Modi</p> -->
							<!-- TODO -->
	
	
							<h4 ref="heading_18" id="heading_18">Tryby automatyczne</h4>
							<p>Są 3 tryby automatyczne. Regulacja prękości obrotowej wentylatorów jest w następujący sposób zależna od stężenia nieporządanych substancji w powietrzu:</p>
							<img src="../../assets/docs/automatic_modes.png" class="no-shadow"/>
							<p>Przy dobrej jakości powietrza wentylatory pozostają wyłączone – wietrzenie nie jest potrzebne. Po przekroczeniu stężeń progowych C1, C2 albo C3, urządzenia wentylacyjne starują z minimalną prędkością obrotową. Następnie zależność intensywności wymiany powietrza od stężenia jest liniowa, aż do osiągnięcia maksymalnej wydajności.</p>
							<p>Tryb „AUTO (fresh)“ jest ustawiony na maksymalne wietrzenie, „AUTO (silent)“ na cichą pracę przy mniejszej ilości powietrza. „AUTO“ jest kompromisem między tymi dwoma trybami. Przy zadanym średnim stężeniu, odstęp strumienia powietrza między trybami wynosi 10%, czyli między „fresh“ a „silent“ 20%.</p>
							<p>Urządzenia, które nie umożliwiają bezstopniowej regulacji prędkości obrotowej wentylatora, przełączają ją skokowo i podążają za podaną charakterystyką w miarę ich możliwości.</p>
	
							<h4 ref="heading_19" id="heading_19">Sterowanie ręczne</h4>
							<p>Sterowanie ręczne umożliwia sterowanie prędkości obrotowej wentylatorów w zakresie od 0% (minimalna prękość obrotowa danego modelu urządzenia wentylacyjnego) do 100% (maksimum prędości obrotowej) w krokach 10%. Sterowanie następuje za pomocą przycisków „ + “ i „ - “.</p>
							<!-- <p>Bild Manual Mode</p> -->
							<!-- TODO -->
	
	
							<h4 ref="heading_20" id="heading_20">Tryb nocny</h4>
							<p>Tryb nocny wprowadzono by urządzenia pracowały najciszej. Pracują w sposób stały z najniższą prędkością obrotową, czyli bez regulacji. </p>
	
	
							<h4 ref="heading_21" id="heading_21">Tryb wietrzenia (intensywny)</h4>
							<p>Tryb „storm mode“ inicjuje intensywne wietrzenie pomieszczenia. Połączone urządzenia wentylacyjne działają przez zadany czas z maksymalną wydajnością, po czym wracają do poprzedniego trybu i wydajności. </p>
							<p>Tryb jest polecany np. w czasie przerw w szkole albo w naradzie. Albo w domu wychodząc z domu, po gotowaniu itp.</p>
	
							<h4 ref="heading_22" id="heading_22">Tryb letni</h4>
							<p>Tryb letni umożliwia wietrzenie bez odzysku ciepła, a nawet uzyskanie pewnego efektu pasywnego chłodzenia. Idea jest taka, by pobierać powietrze z chłodniejszej strony, np. zacienionej, północnej domu i oddawać je w innym kierunku. Stosuje się do tego 2 urządzenia, albo jedno w połączeniu z częściowio otwartym oknem.</p>
	
							<p class="message">
								<b>UWAGA:</b> <br>
								Nie stosować w połączeniu z klimatyzacją. <br>
								W tym przypadku odzysk zimna jest konieczny.
							</p>
	
							<p>Rekuperatory, które w innych trybach przełączają periodycznie kierunek tłoczenia powietrza, przacują w trzybie letnim tylko w jednym kierunku (ustawiamym w czasie instalacji w urządzeniach). W rekuperatorzch wyposażonych w dwa wentylatory, które jednocześnie tłoczą świeże powietrze i usuwają zużyte, jeden z tych wentylatorów zostaje wyłączony.</p>
							<!-- <p>Über Einstellung des Sommermodus lesen Sie in Kapitel ####.</p> --> <!-- TODO -->
	
	
							<h4 ref="heading_23" id="heading_23">Usuwanie wilgoci</h4>
							<p>Wentylacja usuwa nadmierną wilgotność z pomieszczeń. Istnieją jednak sytuacje, kiedy jakość powietrza jest dobra i wentylacja nie byłaby potrzebna, ale wilgotność stwarza taką potrzebę. HAQCS-monitor zwiększa intensywność wentylacji, kiedy ustawiony przez użytkownika próg wilgotności zostanie przekroczony. Urządzenia wentylacyjne zostają o tym powiadomione i działąją w odpowiednim trybie.</p> <!--Über die Einstellung der Schwellenfeuchte lesen Sie im Kapitel ####.</p> --> <!-- TODO -->
							<p>Regulacja następuje według poniższego algorytmu:</p>
							<p>Kiedy osiągnięty zostanie próg wilgotności „F“, aktywuje się tryb usuwania wilgoci. Regulator oblicza odpowiednią prędkość obrotową na podstawie aktualnej wilgotności. Jeśli obliczona prędkość obrotowa jest wyższa od tej wynikającej z pomiarów jakości powietrza, przekazuje do wentylatorów tę wyższą wartość.</p>
							<img src="../../assets/docs/humidity_threshold.png" class="no-shadow"/>
	
	
							<h4 ref="heading_24" id="heading_24">Przerwa w zasilaniu</h4>
							<p>Po zmianie ustawień, zostają one po 5 min. zapisane w pamięci. W przypadku utraty i powrotu zasilania, HAQCS-monitor startuje z zapamiętanymi ustawieniami i tryb oracy zostaje odtworzony.</p> <!-- TODO -->
	
						</v-container>
					</div>
				</div>

				<div style="height:50vh" />
			</div>
		</v-container>
	</div>
</template>

<script>
export default {
	name: 'Dokumentation_HAQCS',
	mounted: function() {
		this.updateHeadings();
		this.updateViewedHeading();
		this.viewedHeadingInterval = setInterval(() => { this.updateViewedHeading(); }, 50);

		setTimeout(() => {
			this.scrollToRoutedHeading();
		}, 400);

		this.unwatchLanguage = this.$watch('language', () => { this.updateHeadings(); this.updateLng(); });
	},
	beforeDestroy() {
		clearInterval(this.viewedHeadingInterval);
		this.unwatchLanguage();
	},
	data () {
		return {
			headings: [],
			// headingsLng: '',
			updateHeadings: function() {
				if (this.headings.length != 0) {
					this.headings = [];
				}

				let currentIndex = 0;
				let reference = "heading_0";
				while (this.$refs[reference] != undefined) {
					let element = {};
					element.text = this.$refs[reference].innerText;
					element.id = reference;
					element.nodeName = this.$refs[reference].nodeName;
					// for (let i = 0; i < 100; i++)
						this.headings.push(element);

					currentIndex += 1;
					reference = "heading_" + String(currentIndex);
				}
				// this.headings = document.getElementById("mainDoc").find("h1");

				this.headingsLng = this.language;
			},
			viewedHeading: "heading_0",
			viewedHeadingInterval: -1,
			updateViewedHeading: function() {
				// if (this.headingsLng !== this.language) {
				// 	this.updateHeadings();
				// }

				if (typeof(this.updateViewedHeading.lastFromTop) == 'undefined') {
					this.updateViewedHeading.lastFromTop = fromTop;
				}

				// let fromTop = window.scrollY;
				let fromTop = document.getElementById('frame').scrollTop;

				if (fromTop == this.updateViewedHeading.lastFromTop) {
					return;
				}
				this.updateHeadings.lastReference = fromTop;

				this.viewedHeading = "heading_0";

				let currentIndex = 0;
				let reference = "heading_0";
				let lastReference = "heading_0";
				while (this.$refs[reference] != undefined) {
					let pos = this.$refs[reference].offsetTop;
					
					if (pos > fromTop + 64) {
						this.viewedHeading = lastReference;
						return;
					}

					lastReference = reference;
					currentIndex += 1;
					reference = "heading_" + String(currentIndex);
				}

				// last heading reached
				this.viewedHeading = lastReference;
			},
			routedHeadings: {
				"Aufstellung": "heading_1",
				"Raumklima": "heading_7",
				"Geschichte": "heading_8",
				// "LED-Anzeige": "TODO",
				"Alarme": "heading_9",
				"Moduswahl": "heading_16",
				"Sturmmodus": "heading_20",
				"Entfeuchtung": "heading_22",
			},
			scrollToRoutedHeading: function() {
				if (this.routedHeadings.hasOwnProperty(this.$route.params.heading)) {
					this.scrollTo('#' + this.routedHeadings[this.$route.params.heading]);
				}
			},
			scrollTo: function(element) {
				let options = {
					// container: 'body',
					container: '#frame',
					easing: 'ease',
					offset: -64,
					force: true,
					cancelable: false,
					onStart: false,
					onDone: false,
					onCancel: false,
					x: true,
					y: true
				};

				let duration = 700;

				this.$scrollTo(element, duration, options);
			},


			// language
			updateLng: function() {
			    this.lng = this.messages[this.language];
			},
			updateLngLastLng: '',
			lng: {},
			messages: {
			    en: {
					title: 'Documentation',
			        description: 'Documentation of the HAQCS-Monitor, a digital device that measures the air quality and indoor climate of a room and optionally controls up to 16 ventilation units and humidifiers over an ad-hoc wireless network.',
					back: 'back',
					backRoute: '/docs',
			    },
			    de: {
					title: 'Dokumentation',
			        description: 'Dokumentation des HAQCS-Monitors, eines digitalen Geräts, das die Luftqualität und das Raumklima eines Raums misst und optional bis zu 16 Lüftungsgeräte und Luftbefeuchter über ein drahtloses Ad-hoc-Netzwerk steuert.',
					back: 'zurück',
					backRoute: '/dokumentation',
				},
				pl: {
					title: 'Dokumentacja',
					description: 'Dokumentacja monitora HAQCS, cyfrowego urządzenia, które mierzy jakość powietrza i klimat wewnętrzny w pomieszczeniu i opcjonalnie kontroluje do 16 jednostek wentylacyjnych i nawilżaczy za pośrednictwem bezprzewodowej sieci ad-hoc.',
					back: 'powrót',
					backRoute: '/dokumentacja',
				}
			},
			getTitle: function() {
				if (this.lng.title === undefined) {
					this.updateLng();
				}
				
				return this.lng.title;
			},
			getMetaDescription: function() {
				if (this.lng.description === undefined) {
					this.updateLng();
				}

				return this.lng.description;
			},
		}
	},
	props: [ 'language' ],
	metaInfo() {
		return {
			title: this.getTitle(),
			// title: "Dokumentation",
			titleTemplate: '%s | Haplabit',
			htmlAttrs: {
			    lang: this.language,
			},
			meta: [
			    { name: 'description', content: this.getMetaDescription() },
			    { name: 'theme-color', content: '#00c853' }
			]
		}
	}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
#sidebar {
	--var-sidebar-width: 256px; /* make sure deplicates have equal value */

	position: -webkit-sticky;
	position: sticky;
	/* position: relative;
	display: inline-block;
	vertical-align: top; */
	
	width: var(--var-sidebar-width);
	/* background: rgb(97, 179, 255); */

	height: 80vh;

	overflow-y: auto;
	padding: 16px 0 16px;
}

#sidebar .unviewed-heading-H2 {
	color: black;
	font-weight: 100;
	cursor: pointer;
}

#sidebar .unviewed-heading-H2:hover {
	text-decoration: underline;
}

#sidebar .viewed-heading-H2 {
	color: black;
	font-weight: bold;
	cursor: pointer;
}

#sidebar .unviewed-heading-H3 {
	padding-left: 8px;
	color: black;
	font-weight: 100;
	cursor: pointer;
}

#sidebar .unviewed-heading-H3:hover {
	text-decoration: underline;
}

#sidebar .viewed-heading-H3 {
	padding-left: 8px;
	color: black;
	font-weight: bold;
	cursor: pointer;
}

#sidebar .unviewed-heading-H4 {
	padding-left: 16px;
	color: black;
	font-weight: 100;
	cursor: pointer;
}

#sidebar .unviewed-heading-H4:hover {
	text-decoration: underline;
}

#sidebar .viewed-heading-H4 {
	padding-left: 16px;
	color: black;
	font-weight: bold;
	cursor: pointer;
}

@media (max-width: 599px) {
	#main {
		padding-left: 0;
		margin-top: 0;
	}
}

@media (min-width: 600px) {
	#main {
		--var-sidebar-width: 256px; /* make sure deplicates have equal value */

		padding-left: var(--var-sidebar-width);
		margin-top: -80vh;
	}
}

#main .content {
	background: white;
}

.content {
	padding: 0 8px;
}

h1 {
	color: black;
	font-size: 2.2rem;
	font-weight: bold;
	font-family: Arial, Helvetica, sans-serif;
	margin-top: 8px;
	margin-bottom: -32px;
}

h2 {
	padding-top: 48px;
	margin-bottom: 8px;
	color: black;
	font-size: 2rem;
	font-weight: 100;
	font-family: Arial, Helvetica, sans-serif;
}

h3 {
	padding-top: 32px;
	margin-bottom: 4px;
	color: black;
	font-size: 1.8rem;
	font-weight: 100;
	font-family: Arial, Helvetica, sans-serif;
}

h4 {
	padding-top: 24px;
	margin-bottom: 2px;
	color: black;
	font-size: 1.5rem;
	font-weight: 100;
	font-family: Arial, Helvetica, sans-serif;
}

ul {
	margin-top: 8px;
	padding-bottom: 16px;
}

ol {
	margin-top: 8px;
	padding-bottom: 16px;
}

li {
	padding-bottom: 8px;
}

.narrow {
	padding-bottom: 0;
}

p {
	text-align: justify;
}

img {
	display: block;
	margin-left: auto;
	margin-right: auto;
	margin-top: 8px;
	margin-bottom: 16px;
	box-shadow: 0 2px 4px rgb(0, 0, 0, 0.38);
	max-width: 100%;
	max-height: 100%;
}

.no-shadow {
	box-shadow: 0 0 0 rgb(0, 0, 0, 0);
}

.link {
	cursor: pointer;
	color:rgb(11, 112, 180);
}

.link:hover {
	color:rgb(8, 87, 139);
	text-decoration: underline;
}

.message {
	margin: 12px 5%;
	text-align: left;
}

.between-headings {
	margin-top:-32px;
}

::selection {
	background: #A7F7A6;
}
</style>



// WEBPACK FOOTER //
// Dokumentation_HAQCS.vue?682f139b